import { MPUT } from 'common/helpers.ts';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';
import type { Shipment } from 'types/Shipment.ts';
import { Button } from 'ui/component/Button.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { TextArea } from 'ui/control/TextArea.js';
import { getTranslatedError } from '#admin/helpers.ts';

type Props = {
  shipment: Shipment;
  onSave?: () => void;
  onClose?: () => void;
  isOpen: boolean;
};

type FormValues = {
  instructions: string;
};

export const ShipmentInstructionsEditDialog = ({ shipment, onSave, onClose, isOpen }: Props) => {
  const { t } = useTranslation();
  const updateShipmentMutation = useSWRMutation(`/shipment/${shipment.id}`, MPUT);
  const isLoading = updateShipmentMutation.isMutating;

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    shouldUnregister: false,
    defaultValues: {
      instructions: shipment.instructions || '',
    },
  });

  const { handleSubmit, formState } = formMethods;

  const onSubmit = async (values: FormValues) => {
    if (!formState.isDirty) return;

    await updateShipmentMutation.trigger({ instructions: values.instructions || null });
    onSave?.();
  };

  return (
    <Modal key={shipment.id} title={<h4>{t('dialogs.shipment.title')}</h4>} isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <form className="flex w-[550px] flex-col gap-2.5 p-2" onSubmit={handleSubmit(onSubmit)}>
          <TextArea name="instructions" label={t('dialogs.shipment.fields.instructions')} disabled={isLoading} />

          <div className="mt-4 flex gap-5">
            <Button type="button" disabled={isLoading} className="blue-outlined" onClick={onClose}>
              {t('actions.back')}
            </Button>
            <Button type="submit" disabled={!formState.isDirty} loading={isLoading} className="blue flex items-center justify-center">
              {t('actions.save')}
            </Button>
          </div>
          {updateShipmentMutation.error && (
            <div className="red mt-2">{getTranslatedError(updateShipmentMutation.error, t) || 'An error occurred and your request could not be completed.'}</div>
          )}
        </form>
      </FormProvider>
    </Modal>
  );
};
